import {AfterViewInit, Component, DoCheck, EventEmitter, Injector, Input, OnInit, Output} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  NgControl,
  Validators
} from '@angular/forms';
import * as _ from 'lodash';
import {combineLatest, Observable, Subject} from "rxjs";
import {debounceTime, map, switchMap} from "rxjs/operators";
import { HttpMethod } from 'src/shared/constants/http-method.enum';
import { HttpCustomService } from 'src/service/http-custom.service';
import { inputValidationMessage } from 'src/shared/validator/helper';

export interface LazyLoadConfig {
  url: string;
  method?: HttpMethod;
  map: any;
  options: any;
  body: any;
}
@Component({
    selector: 'dcu-select',
    templateUrl: './select.component.html',
    styleUrls: ['./select.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: SelectComponent,
        multi: true
    }]
})
export class SelectComponent implements OnInit, ControlValueAccessor, AfterViewInit, DoCheck {
    @Input() loading = false;
    @Input() dropdownList = [];
    @Input() multiple = false;
    @Input() filter = false;
    @Input() bindingKey = false;
    @Input() customLabel = 'Please Select';
    @Input() selectId: string;
    @Input() badgeShowLimit = 3;
    @Input() label = '';
    @Input() tooltipLabel = '';
    @Input() lazyLoad = false;
    @Input() lazyLoadParameter$: Observable<{url?: string, method?: HttpMethod, map?: () => any, options: any, body?: any}>;
    @Input() customSearch = false;
    selectedItems = [];
    dropdownSettings;
    @Input()
    required = false;

    @Input()
    disabled = false;

    @Input()
    enableCheckAll = true;

    inputControl: FormControl  = new FormControl(null);
    parentControl: AbstractControl;
    validationMessage = '';
    onChange;

    @Output() fetchMoreValue = new EventEmitter();

    @Output() storedItemsChanged = new EventEmitter();
    @Output() itemChanged = new EventEmitter();
    @Output() clearValue = new EventEmitter();
    @Output() writeValueChanged = new EventEmitter();
    @Output() initDropdownList = new EventEmitter();

    storedValues = [];
    isSelectAll = false;
    lazyLoadConfig: LazyLoadConfig;
    private page = 0;
    private fetch = new Subject();
    ngUnsubscribe: Subject<boolean> = new Subject<boolean>();
    constructor(private injector: Injector, private http: HttpCustomService) {}

    ngOnInit() {
        this.buildDropDownSetting();
        this.dropdownSettings['disabled'] = this.disabled;
        if (this.multiple) {
            this.dropdownSettings['singleSelection'] = false;
            this.dropdownSettings['selectAllText'] = 'Select All';
            this.dropdownSettings['showCheckbox'] = true;
            this.dropdownSettings['enableCheckAll'] = this.enableCheckAll ;
        } else {
          this.dropdownSettings['selectAllText'] = null;
          this.dropdownSettings['enableCheckAll'] = false;
          this.dropdownSettings['singleSelection'] = true;
          this.dropdownSettings['text'] = this.customLabel;
        }

        if (this.bindingKey) {
          this.dropdownSettings['bindingKey'] = 'bindingKey';
        }

        /*if (this.filter) {
            this.dropdownSettings['enableSearchFilter'] = this.filter;
        }*/
        if (this.required) {
          if(this.multiple) {
            this.inputControl.setValidators(ValidatorMultipleRequired());
          }
          this.inputControl.setValidators([Validators.required]);
        }
        if(this.badgeShowLimit){
          this.dropdownSettings['badgeShowLimit'] = this.badgeShowLimit;
        }
        if(this.customSearch){
          this.dropdownSettings['customSearch'] = true;
        }
        if (this.lazyLoad) {
          this.loadData(this.lazyLoadParameter$);
        }
    }

    writeValue(value) {
      if (value) {
        // if (this.multiple) {
          this.storedValues = _.clone(value);
          this.storedItemsChanged.emit(this.storedValues);
        // }
        this.writeValueChanged.emit(value);
        let selectedItems = [];
        if (_.isArray(value)) {
          selectedItems = value;
        } else {
          selectedItems = [value];
        }

        if (this.lazyLoad) {
          this.selectedItems = selectedItems;
        } else {
          if (this.dropdownList && _.size(this.dropdownList) > 0) {
            let foundItems;
            if (_.isArray(value))  {
              // If dropdownList is Array of Object we should map to Array Id then logic includes is working
              const idSelected = selectedItems.map(item => item.id);
              const listIdDropdown = this.dropdownList.map(item => item.id);
              foundItems = listIdDropdown.filter(item => idSelected.includes(item));
            } else {
              foundItems = this.dropdownList.filter(item => selectedItems.includes(item.id));
            }
            if (foundItems && _.size(foundItems) > 0) {
              this.selectedItems = selectedItems;
              this.inputControl.setValue(this.selectedItems);
            } else {
              if (this.parentControl) {
                this.parentControl.setValue(null);
              }
            }
          }
        }
      } else {
        this.storedValues = [];
        this.inputControl.setValue(value);
      }
  }

  registerOnChange(fn) {
      this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
  }

  onItemSelect(item: any) {
      if (this.onChange) {
        // this.storedValues.push(item);
        if (item && this.multiple) {
          // let foundItem: any = this.storedValues.filter(value => value.id === item.id);
          const foundIndex = _.findIndex(this.storedValues, value => value.id === item.id && value.connectedNo && value.connectedNo > 0);
          // existed => just change deleted
          if (foundIndex >= 0) {
            this.storedValues[foundIndex].deleted = false;
          } else { // add new
            this.storedValues.push(item);
          }
          this.storedItemsChanged.emit(this.storedValues);
          this.itemChanged.emit(item);
        }
        if (this.multiple) {
          this.selectedItems = this.storedValues.filter(value => !value.deleted);
          this.onChange(this.selectedItems);
        } else {
          // for single
          this.onChange(item.id);
          this.storedItemsChanged.emit(item);
        }
      }
  }

  OnItemDeSelect(item: any) {
    this.parentControl.markAsTouched();
    if (this.onChange) {
      if (item && this.multiple) {
        const foundIndex = _.findIndex(this.storedValues,
            value => value.id === item.id && value.connectedNo && value.connectedNo > 0);

        // existed => just change deleted
        if (foundIndex >= 0) {
          this.storedValues[foundIndex].deleted = true;
        } else { // remove item not associated
          _.remove(this.storedValues, value => value.id === item.id);
        }
        this.storedItemsChanged.emit(this.storedValues);
        this.itemChanged.emit(item);
      }
      if (this.multiple) {
        this.onChange(this.selectedItems);
      } else {
        this.selectedItems = [item];
        this.onChange(item.id);
      }
    }
  }

  customDeSelect(items: any[]) {
    for (const item of items) {
      const foundIndex = _.findIndex(this.storedValues, value => value.id === item.id && value.connectedNo > 0);
      // existed => just change deleted
      if (foundIndex >= 0) {
        this.storedValues[foundIndex].deleted = true;
      } else {
        _.remove(this.storedValues, value => value.id === item.id);
      }
    }
  }

  onSelectAll(items: any) {
    this.isSelectAll = true;
    if (this.onChange) {
      if (items && this.multiple) {
        for (const item of items) {
          const foundIndex = _.findIndex(this.storedValues, value => value.id === item.id);
          if (foundIndex >= 0) {
            this.storedValues[foundIndex].deleted = false;
          }
          // add new
          else {
            this.storedValues.push(item);
          }
        }
        this.storedItemsChanged.emit(this.storedValues);
      }
      this.selectedItems = this.storedValues.filter(value => !value.deleted);
      this.onChange(this.selectedItems);
    }
  }

  onDeSelectAll(items: any) {
    this.isSelectAll = false;
      this.parentControl.markAsTouched();
      if (this.onChange) {
        if (this.multiple) {
          const cloneItems = _.clone(this.storedValues);
          const foundExistedNos = _.filter(cloneItems, item => item.connectedNo > 0);
          if (foundExistedNos && _.size(foundExistedNos) > 0) {
            this.storedValues = foundExistedNos;
            _.forEach(this.storedValues, value => {
              _.set(value, 'deleted', true);
            });
          } else {
            this.storedValues = [];
          }
          this.storedItemsChanged.emit(this.storedValues);
          this.onChange(null);
        } else {
          this.storedItemsChanged.emit(null);
          this.onChange(null);
          this.parentControl.setValue(null);
        }
        this.clearValue.emit(true);
      }
  }

  fetchMore(event) {
    this.fetch.next(event);
  }

  loadData(lazyLoadParameter$, searchParams?: any) {
    combineLatest([this.fetch.asObservable(), lazyLoadParameter$]).takeUntil(this.ngUnsubscribe).pipe(debounceTime(500), switchMap((values: any[]) => {
      this.loading = true;
      const lazyLoadParameter = values[1];
      const options = searchParams ? {page: this.page, size: 10, q: searchParams}: {page: this.page, size: 10};
      if (lazyLoadParameter.options) {
        Object.keys(lazyLoadParameter.options).forEach(key => {
          options[key] = lazyLoadParameter.options[key];
        });
      }
      this.lazyLoadConfig = _.cloneDeep(lazyLoadParameter);
      switch (lazyLoadParameter.method) {
        case HttpMethod.POST:
          return this.http.post({
            path: lazyLoadParameter.url
          }, lazyLoadParameter.body, {params: options} ).pipe(map(resp => lazyLoadParameter.map(resp)));
          break;
        default:
          return this.http.get({
            path: lazyLoadParameter.url
          }, options).pipe(map(resp => lazyLoadParameter.map(resp)));
          break;
      }
    })).subscribe(records => {
      if (records && records.length) {
        records.forEach(record => {
          this.dropdownList.push(record);
          if(this.isSelectAll){
            this.storedValues.push(record);
            this.selectedItems.push(record);
          }
        });
        this.page ++;
      }
      this.initDropdownList.emit();
      this.loading = false;
    });
  }

  resetLazyLoad(): boolean {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.ngUnsubscribe = new Subject<boolean>();
    this.page = 0;
    this.dropdownList = [];
    this.isSelectAll = false;
    return true;
  }
  onSearch(event: string){
    this.resetLazyLoad();
    this.loadData(this.lazyLoadParameter$,  event.toLowerCase());
  }

  setDisabledState(disabled: boolean) {
    if (disabled) {
      this.inputControl.disable();
    } else {
      this.inputControl.enable();
    }
}

ngAfterViewInit(): void {
    this.initDropdownList.emit();
    this.parentControl = this.injector.get(NgControl).control;
    if(this.lazyLoad){
      this.onSearch('');
    }
}

ngDoCheck() {
  if (this.parentControl) {
    this.validationMessage = inputValidationMessage(this.parentControl);
  }
}
private buildDropDownSetting() {
  this.dropdownSettings = {
    // singleSelection: true,
    // badgeShowLimit: 3,
    maxHeight: 210,
    showCheckbox: false,
    enableSearchFilter: true,
    lazyLoading: this.lazyLoad,
    enableCheckAll: true,
    enableFilterSelectAll: false,
    text: this.customLabel
  };
}
}

export function ValidatorMultipleRequired() {
return function validate(control: FormControl) {
  if (control && control.value) {
    let arr =[];
    arr = control.value;
    if(arr.length === 0 || !arr.find(value => value.deleted === false)){
      return {
        required : true
      };
    }
    return null;
  }
};
}
